/**
 * Cross-context dashboard route equivalence map.
 *
 * When the user switches between Creator ↔ Organization in the
 * DashboardSwitcher, we try to land them on the *equivalent* page on
 * the other side instead of always bouncing back to home. Saves a click
 * for the common case "I'm in org Billing, switch to creator Billing".
 *
 * Each entry is symmetric — Creator → Org and Org → Creator both
 * resolve via the same table. Pages with no equivalent (e.g. Teams,
 * which is org-only) fall back to home on the target side.
 */
import { features } from '../../../config/features';

// Symmetric pairs: [creatorPageName, orgPageName]. Either direction
// resolves to the other side.
const PAIRS = [
  ['CreatorHomePage', 'OrganizationHomePage'],
  ['CreatorManageListingsPage', 'OrgManageShowsPage'],
  ['CreatorCalendarPage', 'OrgCalendarPage'],
  ['CreatorConversationsPage', 'OrgConversationsPage'],
  ['CreatorAudiencePage', 'OrgAudiencePage'],
  ['CreatorInsightsPage', 'OrgInsightsPage'],
  ['CreatorSettingsProfilePage', 'OrgSettingsProfilePage'],
  ['CreatorSettingsAssignmentsPage', 'OrgSettingsAssignmentsPage'],
  // The org side is not-found while plans are off (plan 6p), so the switcher goes to its home page
  ...(features.paidPlans ? [['CreatorSettingsBillingPage', 'OrgSettingsBillingPage']] : []),
  ['CreatorSettingsNotificationsPage', 'OrgSettingsNotificationsPage'],
  ['CreatorSettingsConfigurationsPage', 'OrgSettingsConfigurationsPage'],
];

const CREATOR_TO_ORG = new Map(PAIRS);
const ORG_TO_CREATOR = new Map(PAIRS.map(([c, o]) => [o, c]));

/**
 * Resolve the route name (and params) to navigate to when switching
 * from the current page to the target context.
 *
 * @param {Object} args
 * @param {string} args.currentPage Name of the page the user is on now
 * @param {string} args.targetContext  'creator' | 'org'
 * @param {string} [args.targetOrgId]  Required when targetContext is 'org'
 * @returns {{ name: string, params?: object }}
 */
export const resolveSwitcherTarget = ({ currentPage, targetContext, targetOrgId }) => {
  if (targetContext === 'creator') {
    const name = ORG_TO_CREATOR.get(currentPage) || 'CreatorHomePage';
    return { name };
  }
  // target = org
  const name = CREATOR_TO_ORG.get(currentPage) || 'OrganizationHomePage';
  return { name, params: { orgId: targetOrgId } };
};
